import React, { useCallback, useEffect, useState } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs'
import { useCompumaticPunchesQuery } from 'queries/admin/timeClock/useCompumaticPunchesQuery'
import { useReprocessCompumaticPunchesMutation } from 'queries/admin/timeClock/useReprocessCompumaticPunchesMutation'
import { useIgnoreCompumaticPunchesMutation } from 'queries/admin/timeClock/useIgnoreCompumaticPunchesMutation'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'

type CompumaticClockPunchesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useCompumaticPunchesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    companyId: number | null
    vendorClockId: string | null
    status: string | null
    selectedPunchIds: number[]
    reprocess: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
    ignore: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
      confirmPunchIds: number[] | null
    }
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setCompanyId: (value: number | undefined) => void
    setVendorClockId: (value: string | undefined) => void
    setStatus: (value: string | undefined) => void
    togglePunchSelected: (punchId: number) => void
    selectPunchIds: (punchIds: number[]) => void
    deselectPunchIds: (punchIds: number[]) => void
    clearSelectedPunchIds: () => void
    reprocessSelectedPunchIds: (onSuccess?: () => void) => void
    reprocessPunchIds: (punchIds: number[]) => void
    openIgnoreConfirm: (punchIds: number[]) => void
    closeIgnoreConfirm: () => void
    confirmIgnorePunchIds: (onSuccess?: () => void) => void
  }
}

const [Context, useCompumaticClockPunchesContextValue] =
  createContext<CompumaticClockPunchesContextState>({
    name: 'CompumaticClockPunchesContext',
  })

export const CompumaticClockPunchesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState()
  const [selectedPunchIds, setSelectedPunchIds] = useState<number[]>([])
  const [ignoreConfirmPunchIds, setIgnoreConfirmPunchIds] = useState<
    number[] | null
  >(null)
  const [filters, setFilters] = useQueryStates(
    {
      companyId: parseAsInteger,
      vendorClockId: parseAsString,
      status: parseAsString,
    },
    {
      urlKeys: {
        companyId: 'company',
        vendorClockId: 'clock',
        status: 'status',
      },
    }
  )

  const clearSelectedPunchIds = useCallback(() => {
    setSelectedPunchIds([])
  }, [])

  useEffect(() => {
    clearSelectedPunchIds()
  }, [
    filters.companyId,
    filters.vendorClockId,
    filters.status,
    pagination.pageIndex,
    pagination.pageSize,
    clearSelectedPunchIds,
  ])

  const setCompanyId = (value: number | undefined) => {
    setFilters({
      companyId: value ?? null,
      vendorClockId: null,
    })
    setPagination({ pageIndex: 0 })
  }

  const setVendorClockId = (value: string | undefined) => {
    setFilters({ vendorClockId: value ?? null })
    setPagination({ pageIndex: 0 })
  }

  const setStatus = (value: string | undefined) => {
    setFilters({ status: value ?? null })
    setPagination({ pageIndex: 0 })
  }

  const togglePunchSelected = (punchId: number) => {
    setSelectedPunchIds((current) =>
      current.includes(punchId)
        ? current.filter((id) => id !== punchId)
        : [...current, punchId]
    )
  }

  const selectPunchIds = (punchIds: number[]) => {
    if (punchIds.length === 0) {
      return
    }
    setSelectedPunchIds((current) =>
      Array.from(new Set([...current, ...punchIds]))
    )
  }

  const deselectPunchIds = (punchIds: number[]) => {
    if (punchIds.length === 0) {
      return
    }
    const idsToRemove = new Set(punchIds)
    setSelectedPunchIds((current) =>
      current.filter((id) => !idsToRemove.has(id))
    )
  }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useCompumaticPunchesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    companyId: filters.companyId ?? undefined,
    vendorClockId: filters.vendorClockId ?? undefined,
    status: filters.status ?? undefined,
  })

  const {
    mutate: reprocessMutate,
    isPending: isReprocessPending,
    isError: isReprocessError,
    error: reprocessError,
  } = useReprocessCompumaticPunchesMutation()

  const {
    mutate: ignoreMutate,
    isPending: isIgnorePending,
    isError: isIgnoreError,
    error: ignoreError,
  } = useIgnoreCompumaticPunchesMutation()

  const reprocessPunchIds = (punchIds: number[]) => {
    if (punchIds.length === 0) {
      return
    }
    reprocessMutate(
      { punchIds },
      {
        onSuccess: () => {
          setSelectedPunchIds((current) =>
            current.filter((id) => !punchIds.includes(id))
          )
        },
      }
    )
  }

  const reprocessSelectedPunchIds = (onSuccess?: () => void) => {
    if (selectedPunchIds.length === 0) {
      return
    }
    reprocessMutate(
      { punchIds: selectedPunchIds },
      {
        onSuccess: () => {
          clearSelectedPunchIds()
          onSuccess?.()
        },
      }
    )
  }

  const openIgnoreConfirm = (punchIds: number[]) => {
    if (punchIds.length === 0) {
      return
    }
    setIgnoreConfirmPunchIds(punchIds)
  }

  const closeIgnoreConfirm = () => {
    setIgnoreConfirmPunchIds(null)
  }

  const confirmIgnorePunchIds = (onSuccess?: () => void) => {
    if (ignoreConfirmPunchIds === null || ignoreConfirmPunchIds.length === 0) {
      return
    }
    const punchIds = ignoreConfirmPunchIds
    ignoreMutate(
      { punchIds },
      {
        onSuccess: () => {
          setSelectedPunchIds((current) =>
            current.filter((id) => !punchIds.includes(id))
          )
          closeIgnoreConfirm()
          onSuccess?.()
        },
      }
    )
  }

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: CompumaticClockPunchesContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      companyId: filters.companyId,
      vendorClockId: filters.vendorClockId,
      status: filters.status,
      selectedPunchIds,
      reprocess: {
        isPending: isReprocessPending,
        isError: isReprocessError,
        errorMessage: parseErrorMessage(reprocessError),
      },
      ignore: {
        isPending: isIgnorePending,
        isError: isIgnoreError,
        errorMessage: parseErrorMessage(ignoreError),
        confirmPunchIds: ignoreConfirmPunchIds,
      },
      pagination,
    },
    actions: {
      refetch,
      setPagination,
      setCompanyId,
      setVendorClockId,
      setStatus,
      togglePunchSelected,
      selectPunchIds,
      deselectPunchIds,
      clearSelectedPunchIds,
      reprocessSelectedPunchIds,
      reprocessPunchIds,
      openIgnoreConfirm,
      closeIgnoreConfirm,
      confirmIgnorePunchIds,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useCompumaticClockPunchesContextValue }
